import { useState, useRef } from 'react';
import api, { downloadCsv } from '../utils/api';

export default function CsvImportModal({ entityLabel, importPath, onClose, onComplete }) {
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  async function handleDownloadTemplate() {
    try {
      await downloadCsv(`${importPath}/template`, `${entityLabel.toLowerCase().replace(/\s+/g, '-')}-template.csv`);
    } catch (e) {
      setError('Could not download template');
    }
  }

  function handleFile(f) {
    if (!f) return;
    setFile(f);
    setError('');
    setPreview(null);
  }

  async function runPreview() {
    if (!file) return;
    setLoading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await api.post(`${importPath}/preview`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(r.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not read this file');
    } finally {
      setLoading(false);
    }
  }

  async function runCommit() {
    setCommitting(true);
    try {
      const r = await api.post(`${importPath}/commit`, { rows: preview.rows });
      setResult(r.data);
      onComplete?.();
    } catch (e) {
      setError(e.response?.data?.error || 'Import failed');
    } finally {
      setCommitting(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="import-modal" onClick={e => e.stopPropagation()}>
        <div className="drawer-header">
          <h3 className="drawer-title">Upload {entityLabel} CSV</h3>
          <button className="drawer-close" onClick={onClose}>×</button>
        </div>

        {!preview && !result && (
          <>
            <div style={{ padding: '20px 24px 0' }}>
              <button className="btn btn-ghost" onClick={handleDownloadTemplate} style={{ padding: 0 }}>
                ↓ Download a blank template with the correct columns
              </button>
            </div>
            <div
              className={'dropzone' + (dragOver ? ' dragover' : '')}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => {
                e.preventDefault();
                setDragOver(false);
                handleFile(e.dataTransfer.files[0]);
              }}
              onClick={() => inputRef.current?.click()}
              style={{ cursor: 'pointer' }}
            >
              <input
                ref={inputRef}
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                onChange={e => handleFile(e.target.files[0])}
              />
              {file ? (
                <div><strong>{file.name}</strong> selected</div>
              ) : (
                <div>
                  Drag a CSV file here, or click to browse.<br />
                  <span className="form-hint">Rows with an id update existing records. New rows without an id are created.</span>
                </div>
              )}
            </div>
            {error && (
              <div style={{ margin: '0 24px 16px', color: 'var(--danger-text)', fontSize: 13 }}>{error}</div>
            )}
            <div className="drawer-footer">
              <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" onClick={runPreview} disabled={!file || loading}>
                {loading ? 'Reading file…' : 'Preview import'}
              </button>
            </div>
          </>
        )}

        {preview && !result && (
          <>
            <div className="import-summary">
              <div className="import-summary-stat">
                <div className="num">{preview.summary.toCreate}</div>
                <div className="label">New records</div>
              </div>
              <div className="import-summary-stat">
                <div className="num">{preview.summary.toUpdate}</div>
                <div className="label">Will update</div>
              </div>
              <div className="import-summary-stat">
                <div className="num" style={{ color: preview.summary.errors > 0 ? 'var(--danger-text)' : undefined }}>
                  {preview.summary.errors}
                </div>
                <div className="label">Errors</div>
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px' }}>
              <table className="data-table" style={{ marginTop: 16 }}>
                <thead>
                  <tr><th>Row</th><th>Action</th><th>Details</th></tr>
                </thead>
                <tbody>
                  {preview.rows.map(r => (
                    <tr key={r.row}>
                      <td>{r.row}</td>
                      <td>
                        {r.action === 'create' && <span className="badge badge-info">Create</span>}
                        {r.action === 'update' && <span className="badge badge-success">Update</span>}
                        {r.action === 'error' && <span className="badge badge-danger">Error</span>}
                      </td>
                      <td style={{ fontSize: 13, color: 'var(--muted)' }}>
                        {r.errors.length > 0 ? r.errors.join('; ') : Object.keys(r.data).join(', ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="drawer-footer">
              <button className="btn btn-secondary" onClick={() => setPreview(null)}>Back</button>
              <button className="btn btn-primary" onClick={runCommit} disabled={committing}>
                {committing ? 'Importing…' : `Confirm import (${preview.summary.toCreate + preview.summary.toUpdate} rows)`}
              </button>
            </div>
          </>
        )}

        {result && (
          <>
            <div style={{ padding: 24 }}>
              <p>Import complete: <strong>{result.created}</strong> created, <strong>{result.updated}</strong> updated
                {result.skipped > 0 && <>, <strong>{result.skipped}</strong> skipped</>}.</p>
            </div>
            <div className="drawer-footer">
              <button className="btn btn-primary" onClick={onClose}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
